import React, { useContext, useEffect, useState } from "react";
import Gender from "../../api/enums/Gender";
import { iconsImgs } from "../../utils/icons";
import { useForm } from "react-hook-form";
import UserProfileSchema, { UserProfileType } from "../../schemas/UserProfileSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { CitiesInfoContext } from "../../AllCitiesContext";
import AuthClient from "../../api/AuthClient";
import GetAllCitiesApi from "../../api/GetAllCitiesApi";
import { parseCitiesResponse } from "../../api/parseResponse/parseAllCitiesResponse";
import EditUserProfileInfoApi from "../../api/EditUserProfileInfoApi";
import { profileInfoUpdateRequestModel } from "../../types";
import { useNavigate } from "react-router";
import { routes } from "../../routes/routes";
import { UserProfileInfoContext } from "../../GlobalContext/UserProfileInfoContext";
import { uploadUserProfileImage } from "../../azureBlob/BlobStorageService";
interface EditProfileFormProps {
  selectedImageFile: File | null;
  onSaveStateChange: (state: RequestState) => void;
}

type RequestState = 'idle' | 'loading' | 'success' | 'error';

const EditProfileForm: React.FC<EditProfileFormProps> = ({ selectedImageFile, onSaveStateChange }) => {

  const { userProfileInfo, setUserProfileFieldValues } = useContext(UserProfileInfoContext);
  const { citiesInfo, setAllCitiesContextData } = useContext(CitiesInfoContext);

  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedGender, setSelectedGender] = useState<Gender>(userProfileInfo.length > 0 ? Number(Gender[userProfileInfo[0]?.Gender]) : 0);
  const [selectedCity, setSelectedCity] = useState<string>(userProfileInfo.length > 0 ? userProfileInfo[0].CityId : '');

  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors }, setValue } = useForm<UserProfileType>({
    defaultValues: {
      firstName: userProfileInfo.length > 0 ? userProfileInfo[0].FirstName : '',
      lastName: userProfileInfo.length > 0 ? userProfileInfo[0].LastName : '',
      dateOfBirth: userProfileInfo.length > 0 && userProfileInfo[0].DateOfBirth ? new Date(userProfileInfo[0].DateOfBirth).toISOString().substring(0, 10) : "",
      email: userProfileInfo.length > 0 ? userProfileInfo[0].Email : '',
      instagram: userProfileInfo.length > 0 ? userProfileInfo[0].Bio : '',
      homeCity: userProfileInfo.length > 0 ? userProfileInfo[0].HomeCity : '',
      cityId: userProfileInfo.length > 0 ? userProfileInfo[0].CityId : '',
      phoneNumber: ''
    }, resolver: zodResolver(UserProfileSchema), mode: "onBlur", shouldFocusError: true
  });

  useEffect(() => {
    if (citiesInfo.length > 0) return;
    const authClient = new AuthClient();
    try {
      const getAllCitiesApi = new GetAllCitiesApi(authClient);
      getAllCitiesApi.getAllCities().then((response) => {
        if (response.data.length > 0) {
          const parsedData = parseCitiesResponse(response.data);
          setAllCitiesContextData([parsedData]);
        }
      });
    } catch (error) {
      console.error("Error fetching cities", error);
    }
  }, [citiesInfo.length, setAllCitiesContextData]);

  useEffect(() => {
    if (userProfileInfo.length > 0) {
      const phoneNumberObj = parsePhoneNumberFromString(userProfileInfo[0].PhonoNumber);
      if (phoneNumberObj) {
        const countryCode = phoneNumberObj.countryCallingCode;
        setPhoneNumber(`+${countryCode}${phoneNumberObj.nationalNumber}`);
      }
    }
  }, [userProfileInfo]);

  useEffect(() => {
    setValue("phoneNumber", phoneNumber.toString().trim(), { shouldValidate: true });
  }, [phoneNumber, setValue]);

  useEffect(() => {
    setValue("cityId", selectedCity, { shouldValidate: true });
  }, [selectedCity, setValue]);

  // Guard: return early if user profile info is not loaded yet
  if (!userProfileInfo || userProfileInfo.length === 0) {
    return <div className="text-center">Loading...</div>;
  }

  const handlePhoneChange = (value?: string) => {
    setPhoneNumber(value ?? '');
  };
  const handleGenderChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedGender(Number(event.target.value) as Gender);
  };
  const handleCityChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCity(event.target.value);
  };

  const onSubmit = async (data: UserProfileType) => {
    onSaveStateChange('loading');

    try {
      const authClient = new AuthClient();
      const editProfileInfoApi = new EditUserProfileInfoApi(authClient);
      const userProfileInfoUpdateModel: profileInfoUpdateRequestModel = {
        Id: userProfileInfo[0].Id,
        FirstName: data.firstName,
        LastName: data.lastName,
        DateOfBirth: new Date(data.dateOfBirth),
        Gender: selectedGender,
        CityId: data.cityId,
        Email: data.email,
        PhoneNumber: data.phoneNumber,
        Bio: data.instagram ? data.instagram : undefined,
        HomeCity: data.homeCity ? data.homeCity : undefined,
        UpdatedImgCount: 0
      };

      const imgCount = await uploadUserProfileImage({
        authClient,
        file: selectedImageFile,
        profileId: userProfileInfo[0].Id,
        profileCount: userProfileInfo[0].CountProfilePictures,
        setAsProfilePhoto: true,
      });

      userProfileInfoUpdateModel.UpdatedImgCount = imgCount ? imgCount : 0;
      await editProfileInfoApi.editUserProfileInfo(userProfileInfoUpdateModel);
      setUserProfileFieldValues(userProfileInfo[0].Id, { FirstName: data.firstName, LastName: data.lastName, CityId: data.cityId })
      onSaveStateChange('success');
      navigate(routes.UserProfile);
    } catch (error) {
      console.log(error);
      onSaveStateChange('error');
    }
  };

  return (
    <>
      <form
        className="m-3"
        onSubmit={handleSubmit(onSubmit, () => onSaveStateChange('idle'))}
        id="user-profile-form"
      >
        <div className="form-group mb-2">
          <input
            type="text"
            className="form-control"
            id="firstName"
            placeholder="First Name"
            {...register("firstName")}
          />
          {errors.firstName && (
            <span className="text-danger">{errors.firstName.message}</span>
          )}
        </div>

        <div className="form-group mb-2">
          <input
            type="text"
            className="form-control"
            id="lastName"
            placeholder="Last Name"
            {...register("lastName")}
          />
          {errors.lastName && (
            <span className="text-danger">{errors.lastName.message}</span>
          )}
          <small className="form-text smallTxt d-flex yellowTxt">
            Not visible to anyone else.
          </small>
        </div>

        <div className="form-group mb-2">
          <input
            type="text"
            className="form-control"
            id="instaBio"
            placeholder="Bio"
            {...register("instagram")}
          />
          {errors.instagram && (
            <span className="text-danger">{errors.instagram.message}</span>
          )}
          <small className="form-text smallTxt d-flex yellowTxt">
            Not visible to anyone else.
          </small>
        </div>

        <div className="form-group mb-2">
          <div className="input-group">
            <PhoneInput
              placeholder="Enter phone number"
              defaultCountry='US'
              value={phoneNumber}
              onChange={handlePhoneChange}
            />
          </div>
          {errors.phoneNumber && (
            <span className="text-danger">{errors.phoneNumber.message}</span>
          )}
          <small className="form-text smallTxt d-flex yellowTxt">
            Not visible to anyone else.
          </small>
        </div>

        <div className="form-group mb-2">
          <input
            type="email"
            className="form-control"
            id="email"
            placeholder="Email"
            {...register("email")}
          />
          {errors.email && (
            <span className="text-danger">{errors.email.message}</span>
          )}
        </div>

        <div className="form-group mb-2">
          <input
            type="date"
            className="yellowTxt form-control"
            id="dob"
            placeholder="Enter Date of Birth"
            {...register("dateOfBirth")}
          />
          {errors.dateOfBirth && (
            <span className="text-danger">{errors.dateOfBirth.message}</span>
          )}
        </div>

        <div className="form-group mb-2 d-flex col-12">
          <select className="custom-select roundedInput yellowTxt order-1 col-12"
            value={selectedGender} onChange={handleGenderChange} id="gender">
            {Object.entries(Gender)
              .filter(([key]) => isNaN(Number(key)))
              .map(([key, value]) => (
                <option key={value} value={value}>
                  {key}
                </option>
              ))}
          </select>
        </div>

        <div className="form-group mb-2 col-12 yellowTxt">
          <div className="d-flex align-items-center">
            <select
              className="custom-select roundedInput yellowTxt order-1 col-6"
              id="city"
              value={selectedCity}
              onChange={handleCityChange}
            >
              <option value="">Select City</option>
              {(citiesInfo[0]?.allCities ?? []).map((city) => (
                <option key={city.Id} value={city.Id}>
                  {city.Name}
                </option>
              ))}
            </select>
            <input type="hidden" {...register("cityId")} />

            <label
              htmlFor="city"
              className="order-2 ms-3 col-6 d-flex align-items-center"
            >
              <img
                src={iconsImgs.World}
                alt="World Icon"
                className="IconInput me-1 ms-3"
              />
              <span className="yellowTxt">Select City</span>
            </label>
          </div>
          {errors.cityId && (
            <span className="text-danger">{errors.cityId.message}</span>
          )}
        </div>

        <div className="form-group mb-3">
          <input
            type="text"
            className="form-control"
            id="homeCity"
            placeholder="Home City"
            {...register("homeCity")}
          />
          {errors.homeCity && (
            <span className="text-danger">{errors.homeCity.message}</span>
          )}
        </div>
      </form>
    </>
  );
};

export default EditProfileForm;
